import { ListingSellerDraftCrudApiClient } from '@steelbuy/api-integration';
import { JsonWebToken } from '@steelbuy/auth';
import { ListingSellerDraftModel, Nullable } from '@steelbuy/ts-shared';
import {
    createCollectionSlice,
    createSelectCollection,
    createSelectCollectionEntity,
    createSelectCreated,
} from '../../util/store/crud/CollectionSlice';
import { CollectionStore } from '../../util/store/crud/CollectionStore';
import { CollectionStoreAccessors } from '../../util/store/crud/CollectionStoreAccessor';
import {
    createCreateThunk,
    createDeleteThunk,
    createEntityServiceThunk,
    createFetchAllThunk,
    createFetchNextThunk,
    createFetchOneThunk,
    createMutateThunk,
} from '../../util/store/crud/Thunks';

// Basic definition
export const listingSellerDraftCollectionStoreName = 'listing-seller-draft-collection-crud';
export type ListingSellerDraftCollectionStore = {
    [listingSellerDraftCollectionStoreName]: CollectionStore<ListingSellerDraftModel>;
};

// Implementation of the async actions
// It is required to declare them before declaring the slice because the block constant has to be defined before using it as the
const createApiClient = (apiBaseUrl: string, jsonWebToken: Nullable<JsonWebToken>) =>
    new ListingSellerDraftCrudApiClient().init(apiBaseUrl, jsonWebToken);
const fetchAll = createFetchAllThunk<
    ListingSellerDraftModel,
    ListingSellerDraftCollectionStore,
    ListingSellerDraftCrudApiClient
>(listingSellerDraftCollectionStoreName, createApiClient);
const fetchNext = createFetchNextThunk<
    ListingSellerDraftModel,
    ListingSellerDraftCollectionStore,
    ListingSellerDraftCrudApiClient
>(listingSellerDraftCollectionStoreName, createApiClient);
const fetchOne = createFetchOneThunk<
    ListingSellerDraftModel,
    ListingSellerDraftCollectionStore,
    ListingSellerDraftCrudApiClient
>(listingSellerDraftCollectionStoreName, createApiClient);
const mutateEntity = createMutateThunk<ListingSellerDraftModel, ListingSellerDraftCrudApiClient>(
    listingSellerDraftCollectionStoreName,
    createApiClient
);
const createEntity = createCreateThunk<ListingSellerDraftModel, ListingSellerDraftCrudApiClient>(
    listingSellerDraftCollectionStoreName,
    createApiClient
);
const deleteEntity = createDeleteThunk<ListingSellerDraftModel, ListingSellerDraftCrudApiClient>(
    listingSellerDraftCollectionStoreName,
    createApiClient
);
const entityService = createEntityServiceThunk<ListingSellerDraftModel, ListingSellerDraftCrudApiClient>(
    listingSellerDraftCollectionStoreName,
    createApiClient
);

// Slice definition
export const listingSellerDraftCollectionSlice = createCollectionSlice<ListingSellerDraftModel>(
    listingSellerDraftCollectionStoreName,
    fetchAll,
    fetchNext,
    fetchOne,
    mutateEntity,
    createEntity,
    deleteEntity,
    entityService
);
const { resetFetchStatus, resetActionStatus } = listingSellerDraftCollectionSlice.actions;

// Selector functions to be used with useSelector or useTypedSelector to read from the state
const selectCollection = createSelectCollection<ListingSellerDraftModel, ListingSellerDraftCollectionStore>(
    listingSellerDraftCollectionStoreName
);
const selectEntity = createSelectCollectionEntity<ListingSellerDraftModel, ListingSellerDraftCollectionStore>(
    listingSellerDraftCollectionStoreName
);
const selectCreated = createSelectCreated<ListingSellerDraftModel, ListingSellerDraftCollectionStore>(
    listingSellerDraftCollectionStoreName
);

// Export the store accessors to be used by the data provider value
export const listingSellerDraftCollectionStoreAccessors = {
    fetchAll,
    fetchNext,
    fetchOne,
    createEntity,
    mutateEntity,
    deleteEntity,
    resetFetchStatus,
    resetActionStatus,
    selectCollection,
    selectEntity,
    selectCreated,
    entityService,
} as CollectionStoreAccessors<ListingSellerDraftModel, ListingSellerDraftCollectionStore>;
